import React from 'react';

const STEP_LABELS = {
  register: 'Registration',
  email: 'Email',
  phone: 'Phone',
  review: 'Admin Review',
};

function DocumentIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 3.75h6l3 3v13.5A1.5 1.5 0 0 1 16.5 21.75h-9A1.5 1.5 0 0 1 6 20.25V5.25A1.5 1.5 0 0 1 7.5 3.75H9Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75h4.5M9.75 13.5h4.5M9.75 17.25h3" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 3.75v3h3" />
    </svg>
  );
}

function MailIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 7.5A2.25 2.25 0 0 1 6 5.25h12A2.25 2.25 0 0 1 20.25 7.5v9A2.25 2.25 0 0 1 18 18.75H6A2.25 2.25 0 0 1 3.75 16.5v-9Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 8.25 6.64 5.08a1.5 1.5 0 0 0 1.82 0l6.64-5.08" />
    </svg>
  );
}

function PhoneIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.1 4.5 10 8.8l-1.4 1.4c.9 1.8 2.4 3.3 4.2 4.2l1.4-1.4 4.3 1.9v3.2c0 .9-.7 1.6-1.6 1.6C9.6 19.7 4.3 14.4 4.3 7.1c0-.9.7-1.6 1.6-1.6h2.2Z" />
    </svg>
  );
}

function ReviewIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className={className} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 11.25 11.25 13.5 15 9.75" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3.75 19.5 7.5v4.75c0 4.2-2.9 7.12-7.5 8-4.6-.88-7.5-3.8-7.5-8V7.5L12 3.75Z" />
    </svg>
  );
}

function StepBubble({ completed, active, tone, label, icon }) {
  const activeClasses = tone === 'dark'
    ? 'border-blue-400 bg-blue-500 text-white shadow-[0_10px_24px_rgba(37,99,235,0.35)]'
    : 'border-blue-600 bg-blue-600 text-white shadow-[0_12px_24px_rgba(37,99,235,0.18)]';
  const completedClasses = tone === 'dark'
    ? 'border-blue-300/70 bg-blue-500/15 text-blue-100'
    : 'border-blue-200 bg-blue-50 text-blue-700';
  const inactiveClasses = tone === 'dark'
    ? 'border-white/20 bg-white/10 text-white/60'
    : 'border-slate-200 bg-slate-100 text-slate-400';
  const labelClasses = tone === 'dark'
    ? active || completed ? 'text-white' : 'text-white/65'
    : active ? 'text-slate-900' : completed ? 'text-slate-700' : 'text-slate-400';

  return (
    <div className="w-14 flex-shrink-0 text-center sm:w-[4.5rem]">
      <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-full border transition-colors sm:h-12 sm:w-12 ${active ? activeClasses : completed ? completedClasses : inactiveClasses}`}>
        {icon}
      </div>
      <p className={`mt-2 text-[10px] font-medium leading-3 sm:text-[11px] sm:leading-4 ${labelClasses}`}>
        {label}
      </p>
    </div>
  );
}

function Connector({ active, tone }) {
  const trackClasses = tone === 'dark' ? 'bg-white/10' : 'bg-slate-200';
  const fillClasses = tone === 'dark' ? 'bg-blue-400' : 'bg-blue-600';

  return (
    <div className="mt-5 h-1 flex-1 overflow-hidden rounded-full sm:mt-6">
      <div className={`h-full w-full ${trackClasses}`}>
        <div className={`h-full transition-all ${fillClasses}`} style={{ width: active ? '100%' : '0%' }} />
      </div>
    </div>
  );
}

export default function RegistrationProgress({ currentStep, tone = 'light', className = '' }) {
  const normalizedStep = currentStep === 'id' ? 'review' : currentStep;
  const steps = [
    { id: 'register', label: STEP_LABELS.register, icon: <DocumentIcon className="h-5 w-5" /> },
    { id: 'email', label: STEP_LABELS.email, icon: <MailIcon className="h-5 w-5" /> },
    { id: 'phone', label: STEP_LABELS.phone, icon: <PhoneIcon className="h-5 w-5" /> },
    { id: 'review', label: STEP_LABELS.review, icon: <ReviewIcon className="h-5 w-5" /> },
  ];
  const currentIndex = Math.max(steps.findIndex((step) => step.id === normalizedStep), 0);

  return (
    <div className={`rounded-2xl border px-3 py-4 sm:px-4 ${tone === 'dark' ? 'border-white/20 bg-white/5' : 'border-slate-200 bg-white'} ${className}`}>
      <div className="flex items-start gap-2">
        {steps.map((step, index) => (
          <React.Fragment key={step.id}>
            <StepBubble
              completed={index < currentIndex}
              active={index === currentIndex}
              tone={tone}
              label={step.label}
              icon={step.icon}
            />
            {index < steps.length - 1 && (
              <Connector active={index < currentIndex} tone={tone} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
